import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { BaseComponent, DocumentPreviewService, ToastService } from '@isophi/core-ui';
import { PapReportsService, PortfolioManagerReportsService } from '@isophi/isophi-pdfs-api';
import { ChildResultCache, EvaluationRule, ManagerTeacher, PapCategoriesService, Tag, TeacherResultsService } from '@isophi/mng-api';
import { TranslocoService } from '@jsverse/transloco';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, catchError, map, Observable, of, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs';

import { HelperService } from '../../../../../../../../apps/management-app/src/app/core/services/helper.service';

@Component({
  selector: 'app-pap-teacher-results',
  templateUrl: './pap-teacher-results.component.html',
  styleUrls: ['./pap-teacher-results.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PapTeacherResultsComponent extends BaseComponent implements OnInit {
  @Input() teacher: ManagerTeacher;

  protected readonly activeModal = inject(NgbActiveModal);
  private readonly teacherResultsService = inject(TeacherResultsService);
  private readonly toastService = inject(ToastService);
  private readonly translocoService = inject(TranslocoService);
  private readonly papCategoriesService = inject(PapCategoriesService);
  private readonly helperService = inject(HelperService);
  private readonly portfolioManagerReportsService = inject(PortfolioManagerReportsService);
  private readonly spinner = inject(NgxSpinnerService);
  private readonly papReportsService = inject(PapReportsService);
  private readonly documentPreviewService = inject(DocumentPreviewService);

  protected teacherResults$: Observable<ChildResultCache[]>;

  protected selectedResultUuid: string | null = null;
  protected selectedResult$ = new BehaviorSubject<ChildResultCache | null>(null);

  protected getCategoryMap = (): Observable<{ [uuid: string]: Tag }> =>
    this.papCategoriesService.papCategoriesList(undefined, undefined, undefined, 1000).pipe(
      map((response) =>
        response.results.map((tag) => {
          return {
            ...tag,
            activeIconUrl: this.helperService.getTagImageUrl(tag.activeIconUrl),
            inactiveIconUrl: this.helperService.getTagImageUrl(tag.inactiveIconUrl),
          };
        })
      ),
      map((categories) => categories.reduce((categoryMap, category) => ({ ...categoryMap, [category.uuid]: category }), {})),
      catchError(() => {
        return of(null);
      })
    );

  protected getCategoryHierarchy = () =>
    this.papCategoriesService.papCategoriesList(undefined, undefined, undefined, 1000).pipe(
      map((response) => {
        const categories = response.results.map((tag) => {
          return {
            ...tag,
            activeIconUrl: this.helperService.getTagImageUrl(tag.activeIconUrl),
            inactiveIconUrl: this.helperService.getTagImageUrl(tag.inactiveIconUrl),
          };
        });
        const superCategories = categories.filter((category) => !category.parentId);
        return superCategories.map((superCategory) => ({
          superCategory,
          subCategories: categories.filter((category) => category.parentId === superCategory.id),
        }));
      }),
      catchError(() => {
        return of([]);
      })
    );

  protected fetchEvaluationRule = (result$: Observable<ChildResultCache | null>): Observable<EvaluationRule> =>
    result$.pipe(
      switchMap((result) =>
        result ? this.teacherResultsService.teacherResultsEvaluationGeneralRuleRetrieve(result.dataSetUuid) : of(null)
      ),
      tap((evaluationRule) => evaluationRule?.ranges?.sort((a, b) => b.code - a.code)),
      catchError(() => {
        return of(null);
      }),
      shareReplay({ refCount: true, bufferSize: 1 })
    );

  ngOnInit(): void {
    this.loadTeacherResults();
  }

  private loadTeacherResults(): void {
    this.teacherResults$ = this.teacherResultsService
      .teacherResultsList(this.teacher.userUuid, undefined, undefined, 'pap_questionnaire')
      .pipe(
        map((results) => results.results),
        tap((results) => {
          if (results.length > 0) {
            this.selectedResultUuid = results[0].dataSetUuid;
            this.selectedResult$.next(results[0]);
          }
        }),
        catchError(() => {
          this.toastService.error(this.translocoService.translate('pap/teacherResults/errorToast'));
          return of([]);
        }),
        takeUntil(this.ngUnsubscribe$)
      );
  }

  protected onResultChange(dataSetUuid: string, results: ChildResultCache[]): void {
    const result = results.find((result) => result.dataSetUuid === dataSetUuid);
    this.selectedResult$.next(result);
  }

  protected downloadTeacherResult() {
    this.spinner.show();

    this.selectedResult$
      .pipe(
        switchMap((selectedResult) => this.papReportsService.getPapTeacherQuestionnaireReport({ dataSetUuid: selectedResult.dataSetUuid })),
        take(1)
      )
      .subscribe({
        next: (data: Blob) => {
          this.documentPreviewService.openBlob(
            data,
            `${this.translocoService.translate('file/TeacherQuestionnaireReportDetail', { lastName: this.teacher?.lastName ?? '' })}.pdf`
          );
        },
        error: (_) => {
          this.toastService.error(this.translocoService.translate('pap/portfolio/printManagerTeacherQuestionnaireReportErrorToast'));
          this.spinner.hide();
        },
        complete: () => {
          this.spinner.hide();
        },
      });
  }
}
